/* Maison Astrélya — JavaScript global (vanilla, sans dépendance). */
(() => {
  'use strict';

  const theme = () => window.theme || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilitaires ---------- */
  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  });

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => { toast.hidden = true; }, 300);
    }, 2600);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
  };

  window.AstrelyaUtils = { fetchConfig, debounce, parseHTML, showToast, updateCartCount };

  /* ---------- Dialogs (menu, recherche, panier, lightbox) ---------- */
  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('is-locked');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.classList.add('is-closing');
    const finish = () => {
      dialog.classList.remove('is-closing');
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 220);
  };

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
    const trigger = dialog._trigger;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (typeof trigger.focus === 'function') trigger.focus({ preventScroll: true });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
        if (dialog.id === 'SearchModal') {
          const input = dialog.querySelector('input[type="search"]');
          if (input) setTimeout(() => input.focus(), 50);
        }
      }
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Clic sur le fond d'un dialog = fermeture
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener('cancel', (event) => {
    if (event.target instanceof HTMLDialogElement) {
      event.preventDefault();
      closeDialog(event.target);
    }
  }, true);

  window.AstrelyaDialog = { open: openDialog, close: closeDialog };

  /* ---------- En-tête fixe ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      if (this.mode === 'none') return;
      this.classList.add('is-sticky');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > this.offsetHeight * 2 && !this.querySelector('details[open]');
        this.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Menus déroulants desktop ---------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-nav-dropdown][open]').forEach((d) => {
      if (!d.contains(event.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-nav-dropdown][open]');
    if (open) {
      open.removeAttribute('open');
      open.querySelector('summary').focus();
    }
  });

  /* ---------- Barre d'annonce ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement-bar__item')];
      if (this.items.length < 2 || reducedMotion.matches) {
        this.classList.add('is-static');
        return;
      }
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 5000;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.next(), this.interval); }
    stop() { clearInterval(this.timer); }
    next() {
      const current = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const nextItem = this.items[this.index];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      nextItem.classList.add('is-active');
      nextItem.removeAttribute('aria-hidden');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Apparition au défilement ---------- */
  const initReveal = (root = document) => {
    const els = root.querySelectorAll('.reveal:not(.is-visible)');
    if (!document.body.classList.contains('has-reveal') || reducedMotion.matches || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  };
  window.AstrelyaReveal = initReveal;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initReveal());
  else initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------- Vidéos : respecter prefers-reduced-motion ---------- */
  if (reducedMotion.matches) {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('video[autoplay]').forEach((v) => { v.pause(); v.removeAttribute('autoplay'); });
    });
  }

  /* ---------- Sélecteur de quantité ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => btn.addEventListener('click', (e) => this.onButton(e)));
    }
    onButton(event) {
      event.preventDefault();
      const prev = this.input.value;
      if (event.currentTarget.name === 'plus') this.input.stepUp();
      else this.input.stepDown();
      if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Formulaire d'ajout au panier ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"][name="add"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    getDrawer() { return document.querySelector('cart-drawer'); }
    async onSubmit(event) {
      const drawer = this.getDrawer();
      if (theme().cartType !== 'drawer' || !drawer) return; // fonctionnement natif : redirection vers /cart
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.setLoading(true);
      this.setError('');

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${theme().routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (data.status) {
          this.setError(data.description || data.message || theme().strings.cartError);
          return;
        }
        drawer.renderFromSections(data.sections);
        drawer.open(this.submitButton);
      } catch (e) {
        this.setError(theme().strings.cartError);
      } finally {
        this.setLoading(false);
      }
    }
    setLoading(state) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', state);
      this.submitButton.setAttribute('aria-disabled', state ? 'true' : 'false');
    }
    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Mise à jour de lignes (tiroir et page) ---------- */
  const changeLine = async (line, quantity, sectionIds) => {
    const body = JSON.stringify({ line, quantity, sections: sectionIds, sections_url: window.location.pathname });
    const response = await fetch(`${theme().routes.cartChange}.js`, { ...fetchConfig(), body });
    const data = await response.json();
    if (data.status || data.errors) throw new Error(data.description || data.message || data.errors || theme().strings.cartError);
    return data;
  };

  const bindLineEvents = (root, onChange) => {
    root.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input[data-line]');
      if (!input) return;
      onChange(Number(input.dataset.line), Number(input.value));
    });
    root.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (!remove) return;
      event.preventDefault();
      onChange(Number(remove.dataset.removeLine), 0);
    });
  };

  /* ---------- Tiroir panier ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      document.querySelectorAll('[data-cart-toggle]').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(link);
        });
      });
      bindLineEvents(this, debounce((line, qty) => this.update(line, qty), 300));
    }
    open(trigger) { openDialog(this.dialog, trigger); }
    renderFromSections(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const doc = parseHTML(sections['cart-drawer']);
      const fresh = doc.querySelector('#CartDrawerInner');
      const current = this.querySelector('#CartDrawerInner');
      if (fresh && current) {
        current.replaceWith(fresh);
        updateCartCount(fresh.dataset.itemCount);
      }
    }
    async update(line, quantity) {
      this.classList.add('is-busy');
      try {
        const data = await changeLine(line, quantity, ['cart-drawer']);
        this.renderFromSections(data.sections);
        const focusTarget = this.querySelector(`.quantity__input[data-line="${line}"]`) || this.querySelector('[data-close-dialog]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      } catch (e) {
        const err = this.querySelector('[data-cart-error]');
        if (err) { err.textContent = e.message; err.hidden = false; }
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Page panier ---------- */
  class CartPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      const updateBtn = this.querySelector('[data-cart-update]');
      if (updateBtn) updateBtn.classList.add('js-hidden');
      bindLineEvents(this, debounce((line, qty) => this.update(line, qty), 350));
    }
    async update(line, quantity) {
      this.classList.add('is-busy');
      try {
        const data = await changeLine(line, quantity, [this.sectionId]);
        const html = data.sections && data.sections[this.sectionId];
        if (html) {
          const fresh = parseHTML(html).querySelector('[data-cart-root]');
          const current = this.querySelector('[data-cart-root]');
          if (fresh && current) current.replaceWith(fresh);
          const btn = this.querySelector('[data-cart-update]');
          if (btn) btn.classList.add('js-hidden');
        }
        updateCartCount(data.item_count);
      } catch (e) {
        const err = this.querySelector('[data-cart-error]');
        if (err) { err.textContent = e.message; err.hidden = false; }
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }
  customElements.define('cart-page', CartPage);

  /* ---------- Recherche prédictive ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (this.dataset.enabled !== 'true' || !this.input || !theme().routes.predictiveSearch) return;
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
    }
    async onInput() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.close(); return; }
      if (this.controller) this.controller.abort();
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'query,product,collection,page,article',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each'
      });
      try {
        const res = await fetch(`${theme().routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = parseHTML(await res.text()).querySelector('#PredictiveSearchContent');
        const markup = html ? html.outerHTML : '';
        this.cache.set(q, markup);
        this.render(markup);
      } catch (e) {
        if (e.name !== 'AbortError') this.close();
      }
    }
    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
    }
    close() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Filtres de collection ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      // Le tri s'applique immédiatement ; les filtres se valident avec « Appliquer ».
      this.form.addEventListener('change', (e) => {
        if (e.target.name === 'sort_by') this.submit();
      });
      const drawer = this.querySelector('[data-facets-drawer]');
      const close = this.querySelector('[data-facets-close]');
      if (drawer) {
        drawer.addEventListener('toggle', () => document.documentElement.classList.toggle('is-locked', drawer.open));
        drawer.addEventListener('keyup', (e) => { if (e.key === 'Escape') { drawer.removeAttribute('open'); drawer.querySelector('summary').focus(); } });
      }
      if (close && drawer) close.addEventListener('click', () => { drawer.removeAttribute('open'); drawer.querySelector('summary').focus(); });
    }
    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      // Supprime les champs de prix vides pour des URL propres
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      window.location.search = params.toString();
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Sélecteur pays / langue ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[data-autosubmit]');
    if (select && select.form) select.form.submit();
  });

  /* ---------- Recommandations produit ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = parseHTML(await res.text());
          const fresh = html.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim().length) {
            this.innerHTML = fresh.innerHTML;
            initReveal(this);
          } else {
            this.closest('.shopify-section')?.classList.add('is-empty');
          }
        } catch (e) { /* silencieux */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else {
        load();
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();
